import { z } from "zod";
import { EmailSchema } from "./auth";

/**
 * `analytics` domain DTOs (Master Spec §31, §33, §34, §40 — Phase 16).
 *
 * §31 is explicit: "Separate: Campaign Progress / Learning Analytics /
 * Assignment Results. Do not reduce all learning data to one score."
 * This file is deliberately narrow because of that rule — it surfaces
 * exactly §40's two semantic-metric vocabularies (Searching vs Sorting),
 * nothing blended from the `progress` domain (`level_progress`'s
 * completed/mastered counts) or the `assignments` domain (submission
 * stats). A teacher wanting "is this student done with the assignment"
 * already has `GET /api/v1/assignments/teaching`; wanting "is this
 * level marked mastered" already has the roster + `GET /api/v1/levels`;
 * this domain answers a different question — "what do this student's
 * actual algorithm operations look like" — and stays in its own lane
 * rather than merging those into one composite view or one score.
 *
 * Searching and Sorting are two separate objects on each student's DTO,
 * never flattened into one shape — a student who has only ever played
 * Sorting levels still gets a `searching` object (all zeros), not an
 * omitted field, so the web UI never has to special-case "this section
 * doesn't exist" vs. "this section is empty."
 */

/** §40's Searching metric list, plus `inspectedElements` distinctly from `comparisons` (see `analytics.repository.ts`'s doc comment for where that one actually lives). */
export const SearchingAnalyticsDTOSchema = z.object({
  attempts: z.number().int().nonnegative(),
  foundCount: z.number().int().nonnegative(),
  notFoundCount: z.number().int().nonnegative(),
  comparisons: z.number().int().nonnegative(),
  inspectedElements: z.number().int().nonnegative(),
  mistakes: z.number().int().nonnegative(),
  hints: z.number().int().nonnegative(),
  totalDurationMs: z.number().int().nonnegative(),
  avgDurationMs: z.number().int().nonnegative(),
});
export type SearchingAnalyticsDTO = z.infer<typeof SearchingAnalyticsDTOSchema>;

/** §40's Sorting metric list verbatim — every field here already lives directly on `game_attempts`, unlike Searching's `inspectedElements`. */
export const SortingAnalyticsDTOSchema = z.object({
  attempts: z.number().int().nonnegative(),
  comparisons: z.number().int().nonnegative(),
  swaps: z.number().int().nonnegative(),
  shifts: z.number().int().nonnegative(),
  passes: z.number().int().nonnegative(),
  mistakes: z.number().int().nonnegative(),
  hints: z.number().int().nonnegative(),
  totalDurationMs: z.number().int().nonnegative(),
  avgDurationMs: z.number().int().nonnegative(),
});
export type SortingAnalyticsDTO = z.infer<typeof SortingAnalyticsDTOSchema>;

export const StudentAnalyticsDTOSchema = z.object({
  studentId: z.string().uuid(),
  email: EmailSchema,
  displayName: z.string(),
  searching: SearchingAnalyticsDTOSchema,
  sorting: SortingAnalyticsDTOSchema,
});
export type StudentAnalyticsDTO = z.infer<typeof StudentAnalyticsDTOSchema>;

/** Querystring for `GET /api/v1/analytics/class?classId=...` — mirrors `ListClassMembersQuerySchema`'s shape exactly. */
export const ClassAnalyticsQuerySchema = z.object({
  classId: z.string().uuid(),
});
export type ClassAnalyticsQuery = z.infer<typeof ClassAnalyticsQuerySchema>;

export const ClassAnalyticsResponseSchema = z.object({
  classId: z.string().uuid(),
  className: z.string(),
  students: z.array(StudentAnalyticsDTOSchema),
});
export type ClassAnalyticsResponse = z.infer<typeof ClassAnalyticsResponseSchema>;
